import type { SceneTimelineKeyPoseFromAPI } from '@cartwhl/common/types';

import type {
  KeyPoseSkeletonSnapshot,
  KeyPoseSourcePosePayload,
} from './types';

const HISTORY_JOB_KEY_POSE_PATHS = [
  ['keyPoses'],
  ['key_poses'],
  ['job', 'keyPoses'],
  ['job', 'key_poses'],
  ['motionEdit', 'keyPoses'],
  ['motionEdit', 'key_poses'],
  ['motion_edit', 'keyPoses'],
  ['motion_edit', 'key_poses'],
  ['data', 'keyPoses'],
  ['data', 'key_poses'],
  ['data', 'job', 'keyPoses'],
  ['data', 'job', 'key_poses'],
] as const;

export type HistoryJobKeyPoseNormalizationResult =
  | { ok: true; keyPoses: SceneTimelineKeyPoseFromAPI[] }
  | { ok: false; error: string };

type NormalizeHistoryJobKeyPosesOptions = {
  allowMissingAsEmpty?: boolean;
};

export function normalizeHistoryJobKeyPoses(
  value: unknown,
  options: NormalizeHistoryJobKeyPosesOptions = {},
): HistoryJobKeyPoseNormalizationResult {
  const extracted = extractHistoryJobKeyPoses(value);
  if (!extracted.found) {
    if (options.allowMissingAsEmpty) {
      return { ok: true, keyPoses: [] };
    }

    return {
      ok: false,
      error: 'This history entry does not include restorable key poses yet.',
    };
  }

  if (!Array.isArray(extracted.value)) {
    return {
      ok: false,
      error: 'History key poses were not returned in a supported format.',
    };
  }

  const keyPoses = normalizeSceneKeyPoses(extracted.value);
  if (keyPoses.length !== extracted.value.length) {
    return {
      ok: false,
      error: 'History key poses were returned in an unsupported format.',
    };
  }

  return { ok: true, keyPoses };
}

export function serializeSceneKeyPoses(keyPoses: unknown) {
  return JSON.stringify(normalizeSceneKeyPoses(keyPoses));
}

export function serializeSceneKeyPoseSourceContent(keyPoses: unknown) {
  return JSON.stringify(
    normalizeSceneKeyPoses(keyPoses).flatMap((keyPose) => {
      const sourcePose = createSourcePoseFromApiKeyPose(keyPose);
      if (!sourcePose) {
        return [];
      }

      return [
        {
          frame: sourcePose.frame,
          localJointRot: sourcePose.localJointRot,
          rootPosition: sourcePose.rootPosition,
          smoothRoot2d: sourcePose.smoothRoot2d,
        },
      ];
    }),
  );
}

export function deserializeSceneKeyPoses(
  payload: string,
): SceneTimelineKeyPoseFromAPI[] {
  try {
    return normalizeSceneKeyPoses(JSON.parse(payload) as unknown);
  } catch {
    return [];
  }
}

export function normalizeSceneKeyPoses(
  keyPoses: unknown,
): SceneTimelineKeyPoseFromAPI[] {
  if (!Array.isArray(keyPoses)) {
    return [];
  }

  return keyPoses.flatMap((keyPose) => {
    const normalizedKeyPose = normalizeSceneKeyPose(keyPose);

    return normalizedKeyPose ? [normalizedKeyPose] : [];
  });
}

export function createSourcePoseFromApiKeyPose(
  keyPose: SceneTimelineKeyPoseFromAPI,
): KeyPoseSourcePosePayload | null {
  const rootPosition = normalizeFiniteVector(keyPose.rootPosition, 3);
  if (!rootPosition) {
    return null;
  }

  const frame = Math.max(Math.round(keyPose.frame), 0);
  const smoothRoot2d = normalizeFiniteVector(keyPose.smoothRoot2d, 2) ?? [
    rootPosition[0]!,
    rootPosition[2]!,
  ];

  return {
    frame,
    localJointRot: normalizeLocalJointRot(keyPose.localJointRot) ?? [],
    rootPosition,
    smoothRoot2d,
  };
}

export function createEmptyKeyPoseSkeletonSnapshot(
  frame: number,
): KeyPoseSkeletonSnapshot {
  return {
    schemaVersion: 1,
    frame,
    transformsByName: {},
  };
}

function normalizeSceneKeyPose(
  value: unknown,
): SceneTimelineKeyPoseFromAPI | null {
  const keyPose = asRecord(value);
  if (!keyPose) {
    return null;
  }

  const sourcePose =
    asRecord(keyPose.sourcePose) ?? asRecord(keyPose.source_pose);
  const frame = toFiniteNumber(keyPose.frame ?? sourcePose?.frame);
  const localJointRot = normalizeLocalJointRot(
    keyPose.localJointRot ??
      keyPose.local_joint_rot ??
      sourcePose?.localJointRot ??
      sourcePose?.local_joint_rot,
  );
  const rootPosition = normalizeFiniteVector(
    keyPose.rootPosition ??
      keyPose.root_position ??
      sourcePose?.rootPosition ??
      sourcePose?.root_position,
    3,
  );
  const smoothRoot2d = normalizeFiniteVector(
    keyPose.smoothRoot2d ??
      keyPose.smoothRoot2D ??
      keyPose.smooth_root_2d ??
      keyPose.smooth_root2d ??
      sourcePose?.smoothRoot2d ??
      sourcePose?.smoothRoot2D ??
      sourcePose?.smooth_root_2d ??
      sourcePose?.smooth_root2d,
    2,
  );

  if (frame === null || !localJointRot || !rootPosition) {
    return null;
  }

  const normalizedKeyPose: SceneTimelineKeyPoseFromAPI = {
    frame: Math.max(Math.round(frame), 0),
    localJointRot,
    rootPosition,
  };
  if (typeof keyPose.id === 'string' && keyPose.id.trim()) {
    normalizedKeyPose.id = keyPose.id.trim();
  }
  if (smoothRoot2d) {
    normalizedKeyPose.smoothRoot2d = smoothRoot2d;
  }

  return normalizedKeyPose;
}

function extractHistoryJobKeyPoses(value: unknown): {
  found: boolean;
  value?: unknown;
} {
  if (Array.isArray(value)) {
    return { found: true, value };
  }

  for (const path of HISTORY_JOB_KEY_POSE_PATHS) {
    const result = getNestedValue(value, path);
    if (result.found && result.value !== undefined) {
      return result;
    }
  }

  return { found: false };
}

function getNestedValue(
  value: unknown,
  path: readonly string[],
): { found: boolean; value?: unknown } {
  let currentValue = value;

  for (const key of path) {
    const currentRecord = asRecord(currentValue);
    if (
      !currentRecord ||
      !Object.prototype.hasOwnProperty.call(currentRecord, key)
    ) {
      return { found: false };
    }

    currentValue = currentRecord[key];
  }

  return { found: true, value: currentValue };
}

function normalizeLocalJointRot(value: unknown): number[][] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.map(
    (axisAngle) => normalizeFiniteVector(axisAngle, 3) ?? [0, 0, 0],
  );
}

function normalizeFiniteVector(
  value: unknown,
  length: number,
): number[] | null {
  if (!Array.isArray(value) || value.length < length) {
    return null;
  }

  const vector = value.slice(0, length).map(toFiniteNumber);
  if (vector.some((number) => number === null)) {
    return null;
  }

  return vector as number[];
}

function toFiniteNumber(value: unknown): number | null {
  const number =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value)
        : NaN;

  return Number.isFinite(number) ? number : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}
